import { useEffect, useState } from "react";
import { docsClient, type UserStats } from "@/lib/supabase";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { FileText, Tag, Clock } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

export default function DashboardPage() {
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const { data, error } = await docsClient.rpc("get_user_stats");

      if (error) throw error;

      // The function returns TABLE, so data is an array - we need the first row
      if (data && data.length > 0) {
        setStats(data[0]);
      } else {
        setStats(null);
      }
    } catch (error) {
      console.error("Error loading stats:", error);
      toast({
        title: "Error",
        description: "Failed to load dashboard statistics",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-foreground">Dashboard</h1>
        <p className="text-muted-foreground mt-2">
          Welcome to LocalDocs. Here's an overview of your documentation.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {/* Total Documents */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Documents</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="text-2xl font-bold text-foreground">
                {stats?.total_documents || 0}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1">Collected documentation</p>
          </CardContent>
        </Card>

        {/* Total Tags */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Unique Tags</CardTitle>
            <Tag className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="text-2xl font-bold text-foreground">{stats?.total_tags || 0}</div>
            )}
            <p className="text-xs text-muted-foreground mt-1">Organization labels</p>
          </CardContent>
        </Card>

        {/* Last Updated */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Last Updated</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-8 w-32" />
            ) : (
              <div className="text-2xl font-bold text-foreground">
                {stats?.last_updated
                  ? formatDistanceToNow(new Date(stats.last_updated), { addSuffix: true })
                  : "Never"}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1">Most recent change</p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common tasks to get you started</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-2 sm:flex-row sm:gap-4">
            <Button asChild>
              <Link to="/documents">View Documents</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/documents?action=add">Add Document</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/exports">Create Export</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
